/**
 * Servicio para gestionar el almacenamiento local del logo de la empresa
 * Usa IndexedDB para almacenar el blob de la imagen
 */
import axiosInstance from '../utils/AxiosConfig';

const DB_NAME = 'axium_dashboard';
const DB_VERSION = 1;
const STORE_NAME = 'logo_storage';

// Inicializar la base de datos
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
};

/**
 * Guarda el logo en IndexedDB
 * @param {Blob} blob - El blob de la imagen del logo
 * @param {number} empresaId - ID de la empresa
 * @returns {Promise<void>}
 */
export const saveLogoLocally = async (blob, empresaId) => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const key = `company_logo_${empresaId}`;
    await new Promise((resolve, reject) => {
      const request = store.put(blob, key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error guardando logo localmente:', error);
    throw error;
  }
};

/**
 * Obtiene el logo guardado localmente
 * @param {number} empresaId - ID de la empresa
 * @returns {Promise<string|null>} - URL del blob o null si no existe
 */
export const getLocalLogo = async (empresaId) => {
  try {
    if (!empresaId) {
      return null;
    }
    
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    const key = `company_logo_${empresaId}`;
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => {
        const blob = request.result;
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          resolve(blobUrl);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error obteniendo logo local:', error);
    return null;
  }
};

/**
 * Elimina el logo guardado localmente
 * @param {number} empresaId - ID de la empresa
 * @returns {Promise<void>}
 */
export const deleteLocalLogo = async (empresaId) => {
  try {
    if (!empresaId) {
      return;
    }
    
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const key = `company_logo_${empresaId}`;
    await new Promise((resolve, reject) => {
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error eliminando logo local:', error);
    throw error;
  }
};

/**
 * Descarga el logo desde S3 y lo guarda localmente
 * @param {number} empresaId - ID de la empresa
 * @returns {Promise<string|null>} - URL del blob o null si no existe
 */
export const downloadAndSaveLogo = async (empresaId) => {
  try {
    if (!empresaId) {
      return null;
    }
    
    const response = await axiosInstance.get('/logo/image', {
      responseType: 'blob',
      skipGlobalErrorRedirect: true,
    });
    
    if (response.status === 200) {
      const blob = response.data;
      await saveLogoLocally(blob, empresaId);
      return URL.createObjectURL(blob);
    }

    return null;
  } catch (error) {
    console.error('Error descargando y guardando logo:', error);
    return null;
  }
};

/**
 * Obtiene el logo, primero desde el almacenamiento local, si no existe lo descarga
 * @param {number} empresaId - ID de la empresa (opcional, se obtiene de localStorage si no se proporciona)
 * @returns {Promise<string|null>} - URL del blob o null si no existe
 */
export const getLogo = async (empresaId = null) => {
  // Obtener empresaId de localStorage si no se proporciona
  const empresaIdToUse = empresaId || parseInt(localStorage.getItem('empresaId'), 10);
  
  if (!empresaIdToUse) {
    return null;
  }
  
  // Primero intentar obtener el logo local
  const localLogo = await getLocalLogo(empresaIdToUse);
  if (localLogo) {
    return localLogo;
  }
  
  // Si no existe localmente, descargarlo y guardarlo
  return await downloadAndSaveLogo(empresaIdToUse);
};
